import React, { useState } from 'react';

import { AlineChip } from 'src/components';
import type { GroupRow } from 'src/components/admin';
import type { Apollo } from 'src/gql/generated';
import { useResponsiveOverflow } from 'src/hooks/dom-hooks';
import type { LabelSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import type {
  DocumentWithLabels,
  LabelEditorPopoverProps,
  WorkflowWithLabels,
} from './LabelEditor';
import {
  DocumentLabelEditor,
  FolderSyncJobLabelEditor,
  GroupLabelEditor,
  WorkflowLabelEditor,
} from './LabelEditor';
import type { LabelOption } from './LabelOption';

const defaultNumDisplayedLabels = 3;
const useStyles = makeStyles()(theme => ({
  /**
   * Single-row layout that fills the available width and clips overflow, so the
   * container's measured width equals the space available for labels.
   */
  fittedRow: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
    justifyContent: 'flex-start',
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
  },

  ghostChip: {
    border: `1px dashed ${theme.appColors.rule}`,
  },
  labelsRow: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    justifyContent: 'flex-start',
    width: 'fit-content',
  },
  /**
   * Wrapper variant used with `fitToWidth`: fills the cell so the fitted row
   * can measure the full available width.
   */
  labelsRowFitted: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    minWidth: 0,
    width: '100%',
  },
  /**
   * Hidden layer that lays out every label at its natural width so we can
   * measure how many fit. Absolutely positioned so it never affects layout.
   */
  measureLayer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
    left: 0,
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: 'max-content',
  },
  /**
   * Keeps the overflow chip at its natural width so a narrow column truncates
   * the label chip rather than the "+ N labels" count.
   */
  overflowChip: {
    flexShrink: 0,
  },
}));

interface LabelListBodyProps {
  allLabels: LabelOption[];
  displayedLabels: LabelOption[];
  remainingCount: number;
  setOpen: (open: boolean) => void;
}

function LabelListBody({
  allLabels,
  displayedLabels,
  remainingCount,
  setOpen,
}: LabelListBodyProps) {
  const { classes } = useStyles();

  if (!allLabels.length)
    return (
      <AlineChip
        chipText="Add label +"
        className={classes.ghostChip}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      />
    );

  return (
    <>
      {displayedLabels.map((label, index) => (
        <AlineChip
          key={index}
          chipText={label.labelText}
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        />
      ))}
      {remainingCount > 0 && (
        <AlineChip
          chipText={`+ ${remainingCount} ${remainingCount === 1 ? 'label' : 'labels'}`}
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        />
      )}
    </>
  );
}

interface FittedLabelListBodyProps {
  allLabels: LabelOption[];
  setOpen: (open: boolean) => void;
}

/**
 * Renders as many labels as fit the available width, spilling the remainder
 * into a "+ N labels" overflow chip. Recomputes automatically when the
 * container (e.g. a resizable grid column) changes width. Always shows at
 * least one label so a narrow column still renders a (truncated) label rather
 * than only the overflow chip.
 */
function FittedLabelListBody({ allLabels, setOpen }: FittedLabelListBodyProps) {
  const { classes } = useStyles();
  const { containerRef, getItemRef, measureRef, overflowRef, visibleCount } =
    useResponsiveOverflow<HTMLDivElement>(allLabels.length, 'horizontal');

  if (!allLabels.length)
    return (
      <AlineChip
        chipText="Add label +"
        className={classes.ghostChip}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      />
    );

  const visibleLabelCount = Math.max(1, visibleCount);
  const displayedLabels = allLabels.slice(0, visibleLabelCount);
  const remainingCount = allLabels.length - displayedLabels.length;

  return (
    <div ref={containerRef} className={classes.fittedRow}>
      <div ref={measureRef} aria-hidden className={classes.measureLayer}>
        {allLabels.map((label, index) => (
          <div key={index} ref={getItemRef(index)}>
            <AlineChip chipText={label.labelText} />
          </div>
        ))}
        <div ref={overflowRef}>
          <AlineChip chipText={`+ ${allLabels.length} labels`} />
        </div>
      </div>
      {displayedLabels.map((label, index) => (
        <AlineChip
          key={index}
          chipText={label.labelText}
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        />
      ))}
      {remainingCount > 0 && (
        <AlineChip
          chipText={`+ ${remainingCount} ${remainingCount === 1 ? 'label' : 'labels'}`}
          className={classes.overflowChip}
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        />
      )}
    </div>
  );
}

interface WorkflowLabelListProps {
  popoverProps: Pick<LabelEditorPopoverProps, 'anchorOrigin' | 'style' | 'transformOrigin'>;
  workflow: WorkflowWithLabels;
  numDisplayedLabels?: number;
}

export function WorkflowLabelList({
  numDisplayedLabels = defaultNumDisplayedLabels,
  popoverProps,
  workflow,
}: WorkflowLabelListProps) {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const sortedLabels = [...workflow.labels].sort((a, b) => a.labelText.localeCompare(b.labelText));
  const displayedLabels = sortedLabels.slice(0, numDisplayedLabels);
  const remainingCount = Math.max(0, sortedLabels.length - numDisplayedLabels);

  return (
    <div ref={setAnchorEl} className={classes.labelsRow}>
      <LabelListBody
        allLabels={sortedLabels}
        displayedLabels={displayedLabels}
        remainingCount={remainingCount}
        setOpen={setOpen}
      />
      <WorkflowLabelEditor
        popoverProps={{
          ...popoverProps,
          anchorEl,
          onClose: () => setOpen(false),
          open,
        }}
        workflow={workflow}
      />
    </div>
  );
}

interface DocumentLabelListProps {
  document: DocumentWithLabels;
  popoverProps: Pick<LabelEditorPopoverProps, 'anchorOrigin' | 'style' | 'transformOrigin'>;
  /**
   * When true, the number of displayed labels is derived from the available
   * width instead of `numDisplayedLabels` — as many labels as fit are shown,
   * with the remainder in a "+ N labels" chip.
   */
  fitToWidth?: boolean;
  numDisplayedLabels?: number;
}

export function DocumentLabelList({
  document,
  fitToWidth = false,
  numDisplayedLabels = defaultNumDisplayedLabels,
  popoverProps,
}: DocumentLabelListProps) {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const sortedLabels = [...document.labels].sort((a, b) => a.labelText.localeCompare(b.labelText));
  const displayedLabels = sortedLabels.slice(0, numDisplayedLabels);
  const remainingCount = Math.max(0, sortedLabels.length - numDisplayedLabels);

  return (
    <div
      ref={setAnchorEl}
      className={fitToWidth ? classes.labelsRowFitted : classes.labelsRow}
      onClick={e => e.stopPropagation()}
    >
      {fitToWidth ? (
        <FittedLabelListBody allLabels={sortedLabels} setOpen={setOpen} />
      ) : (
        <LabelListBody
          allLabels={sortedLabels}
          displayedLabels={displayedLabels}
          remainingCount={remainingCount}
          setOpen={setOpen}
        />
      )}
      <DocumentLabelEditor
        document={document}
        documentLabels={sortedLabels}
        popoverProps={{
          ...popoverProps,
          anchorEl,
          onClose: () => setOpen(false),
          open,
        }}
      />
    </div>
  );
}

interface GroupLabelListProps {
  group: GroupRow;
  popoverProps: Pick<LabelEditorPopoverProps, 'anchorOrigin' | 'style' | 'transformOrigin'>;
}

export function GroupLabelList({ group, popoverProps }: GroupLabelListProps): JSX.Element {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const sortedLabels = [...group.labels].sort((a, b) => a.labelText.localeCompare(b.labelText));

  return (
    <div ref={setAnchorEl} className={classes.labelsRow} onClick={e => e.stopPropagation()}>
      <LabelListBody
        allLabels={sortedLabels}
        displayedLabels={sortedLabels}
        remainingCount={0}
        setOpen={setOpen}
      />
      <GroupLabelEditor
        group={group}
        popoverProps={{
          ...popoverProps,
          anchorEl,
          onClose: () => setOpen(false),
          open,
        }}
      />
    </div>
  );
}

export interface FolderSyncJobWithLabels extends Pick<Apollo.FolderSyncJob, 'id' | '__typename'> {
  labels: LabelSubset[];
}

interface FolderSyncJobLabelListProps {
  folderSyncJob: FolderSyncJobWithLabels;
  popoverProps: Pick<LabelEditorPopoverProps, 'anchorOrigin' | 'style' | 'transformOrigin'>;
  numDisplayedLabels?: number;
}

export function FolderSyncJobLabelList({
  folderSyncJob,
  numDisplayedLabels = defaultNumDisplayedLabels,
  popoverProps,
}: FolderSyncJobLabelListProps) {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const sortedLabels = [...folderSyncJob.labels].sort((a, b) =>
    a.labelText.localeCompare(b.labelText)
  );
  const displayedLabels = sortedLabels.slice(0, numDisplayedLabels);
  const remainingCount = Math.max(0, sortedLabels.length - numDisplayedLabels);

  return (
    <div ref={setAnchorEl} className={classes.labelsRow}>
      <LabelListBody
        allLabels={sortedLabels}
        displayedLabels={displayedLabels}
        remainingCount={remainingCount}
        setOpen={setOpen}
      />
      <FolderSyncJobLabelEditor
        folderSyncJob={folderSyncJob}
        popoverProps={{
          ...popoverProps,
          anchorEl,
          onClose: () => setOpen(false),
          open,
        }}
      />
    </div>
  );
}
